import { useQuery, useMutation } from "@tanstack/react-query";
import { Link, useRoute, useLocation } from "wouter";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, UserCircle, Calendar, Globe, Star, Trash2, ChevronLeft, User } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { calculateAge } from "@shared/utils";
import type { ChildProfile } from "@shared/schema";
import Navigation from "@/components/Navigation";
import Footer from "@/components/ui/footer";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PurchaseChildProfileDialog } from "@/components/PurchaseChildProfileDialog";

function ChildProfilesList() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [deleteProfileId, setDeleteProfileId] = useState<number | null>(null);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);

  // Check for payment status in URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get('payment');
    
    if (paymentStatus === 'success') {
      toast({
        title: "Payment successful!",
        description: "Your child profile has been created.",
      });
      // Clean up URL
      window.history.replaceState({}, '', '/child-profiles');
      // Refresh profiles
      queryClient.invalidateQueries({ queryKey: ['/api/child-profiles'] });
    } else if (paymentStatus === 'cancelled') {
      toast({
        title: "Payment cancelled",
        description: "The payment was cancelled. You can try again.",
        variant: "destructive",
      });
      // Clean up URL
      window.history.replaceState({}, '', '/child-profiles');
    }
  }, [toast]);

  const { data: profiles, isLoading } = useQuery<ChildProfile[]>({
    queryKey: ['/api/child-profiles'],
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest('DELETE', `/api/child-profiles/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/child-profiles'] });
      toast({
        title: "Child profile deleted",
        description: "The child profile has been successfully deleted.",
      });
      setDeleteProfileId(null);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete child profile.",
        variant: "destructive",
      });
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (id: number) => {
      return await apiRequest('POST', `/api/child-profiles/${id}/set-default`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/child-profiles'] });
      toast({
        title: "Default child updated",
        description: "This child is now the default for story generation.",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to set default child.",
        variant: "destructive",
      });
    },
  });

  const getLanguageName = (code: string) => {
    const languages: Record<string, string> = {
      en: "English",
      es: "Spanish",
      fr: "French",
      de: "German",
      zh: "Chinese",
      ja: "Japanese",
      pt: "Portuguese",
      it: "Italian",
      ru: "Russian",
      ar: "Arabic",
    };
    return languages[code] || code;
  };

  const handleAddChild = () => {
    // Check if user already has child profiles
    if (profiles && profiles.length > 0) {
      // Show purchase dialog for additional profiles
      setShowPurchaseDialog(true);
    } else {
      // First profile is free, navigate directly
      navigate("/child-profiles/new");
    }
  };

  const handlePurchaseConfirm = async () => {
    try {
      setShowPurchaseDialog(false);
      
      // Call the purchase slot endpoint
      const response = await apiRequest('POST', '/api/child-profiles/purchase-slot');
      const data = await response.json();
      
      if (data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        toast({
          title: "Error",
          description: "Failed to initiate payment. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to initiate payment. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
        <Navigation />
        <div className="container mx-auto py-8">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-teal-50">
      <Navigation />
      <div className="container mx-auto py-8 px-4 max-w-6xl">
      <div className="mb-8">
        <Link href="/profile">
          <Button variant="ghost" size="sm" className="mb-4" data-testid="button-back-to-profile">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Profile
          </Button>
        </Link>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-teal-600 bg-clip-text text-transparent" data-testid="text-page-title">
              My Children
            </h1>
            <p className="text-muted-foreground mt-2" data-testid="text-page-description">
              Manage profiles for each child to personalize their stories
            </p>
          </div>
          <Button 
            onClick={handleAddChild}
            className="bg-gradient-to-r from-purple-600 to-teal-600" 
            data-testid="button-add-child"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Child
          </Button>
        </div>
      </div>

      {!profiles || profiles.length === 0 ? (
        <Card className="border-2 border-dashed" data-testid="card-empty-state">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <UserCircle className="h-20 w-20 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2" data-testid="text-empty-title">No children added yet</h3>
            <p className="text-muted-foreground mb-6 text-center" data-testid="text-empty-description">
              Add your first child profile to start creating personalized stories
            </p>
            <Button 
              onClick={handleAddChild}
              className="bg-gradient-to-r from-purple-600 to-teal-600" 
              data-testid="button-add-first-child"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Child
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {profiles.map((profile) => {
            const age = profile.birthday ? calculateAge(new Date(profile.birthday)) : null;
            
            return (
              <Card key={profile.id} className="hover:shadow-lg transition-shadow" data-testid={`card-child-${profile.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={profile.avatarUrl || undefined} alt={profile.name} />
                          <AvatarFallback>
                            <User className="h-6 w-6" />
                          </AvatarFallback>
                        </Avatar>
                        <CardTitle className="flex items-center gap-2" data-testid={`text-child-name-${profile.id}`}>
                          {profile.name}
                          {profile.isDefault && (
                            <Badge variant="secondary" className="bg-purple-100 text-purple-700" data-testid={`badge-default-${profile.id}`}>
                              <Star className="h-3 w-3 mr-1" />
                              Default
                            </Badge>
                          )}
                        </CardTitle>
                      </div>
                      <CardDescription className="mt-2 space-y-1">
                        {age !== null && (
                          <div className="flex items-center gap-2" data-testid={`text-child-age-${profile.id}`}>
                            <Calendar className="h-4 w-4" />
                            <span>{age} years old</span>
                          </div>
                        )}
                        {profile.preferredLanguage && (
                          <div className="flex items-center gap-2" data-testid={`text-child-language-${profile.id}`}>
                            <Globe className="h-4 w-4" />
                            <span>{getLanguageName(profile.preferredLanguage)}</span>
                          </div>
                        )}
                        {profile.gender && (
                          <div className="flex items-center gap-2" data-testid={`text-child-gender-${profile.id}`}>
                            <UserCircle className="h-4 w-4" />
                            <span className="capitalize">{profile.gender}</span>
                          </div>
                        )}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Link href={`/child-profiles/${profile.id}`} className="flex-1">
                      <Button variant="outline" className="w-full" data-testid={`button-edit-${profile.id}`}>
                        Edit Profile
                      </Button>
                    </Link>
                    {!profile.isDefault && (
                      <Button
                        variant="outline"
                        onClick={() => setDefaultMutation.mutate(profile.id)}
                        disabled={setDefaultMutation.isPending}
                        data-testid={`button-set-default-${profile.id}`}
                      >
                        <Star className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => setDeleteProfileId(profile.id)}
                      className="text-red-600 hover:text-red-700"
                      data-testid={`button-delete-${profile.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <PurchaseChildProfileDialog
        open={showPurchaseDialog}
        onOpenChange={setShowPurchaseDialog}
        onPurchase={handlePurchaseConfirm}
      />

      <AlertDialog open={deleteProfileId !== null} onOpenChange={(open) => !open && setDeleteProfileId(null)}>
        <AlertDialogContent data-testid="dialog-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="text-delete-title">Delete child profile?</AlertDialogTitle>
            <AlertDialogDescription data-testid="text-delete-description">
              This action cannot be undone. This will permanently delete this child's profile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteProfileId && deleteMutation.mutate(deleteProfileId)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
      <Footer />
    </div>
  );
}

export default ChildProfilesList;
